import { config } from '@littlebird/sdk';
import {
  isNativeSchemeUrl,
  isOpenableUrl,
  isTrustedUrl,
  parseActionCardUrl,
  parseFeedbackDraftUrl,
  parseFileLinkUrl,
  parseSendDraftUrl,
  parseWebhookEndpointSetupUrl,
} from '@littlebird/ui-utils';
import { useMatchRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useContext, type HTMLProps, type ReactNode } from 'react';

import { OAuthConnectionCard } from '@/components/blocks/Message/OAuthConnectionCard';
import { SendDraftCard } from '@/components/blocks/Message/SendDraftCard';
import { SendFeedbackReportCard } from '@/components/blocks/Message/SendFeedbackReportCard';
import { ExternalLinkModal } from '@/components/modals/ExternalLinkModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { ActionCardMentionChip } from '@/features/action-cards/components/ActionCardMentionChip';
import { InlineActionCard } from '@/features/action-cards/components/InlineActionCard';
import { InlineFileLink } from '@/features/file-links/components/InlineFileLink';
import { GroupMentionChip } from '@/features/hivemind/mentions';
import { AppMentionChip } from '@/features/mention';
import { parseAppMentionUrl } from '@/features/mention/mention-links';
import {
  parsePersonMentionUrl,
  PersonMentionChip,
} from '@/features/people-wiki/mentions';
import { WebhookEndpointSetupCard } from '@/features/routines/components/webhook-endpoint-setup-card';
import { Screenshot } from '@/features/screenshots/screenshot';
import { isNewTabClick, useTabs } from '@/features/tabs';
import { isMac } from '@/utils/capabilities';
import { navigate } from '@/utils/navigation-service';
import { parseOAuthUrl } from '@/utils/oauth-url-parser';

import { SmoothdownRenderContext } from '../smoothdown-render-context';

export interface AnchorProps extends HTMLProps<HTMLAnchorElement> {
  children?: ReactNode;
}

export const Anchor = observer(({ children, href }: AnchorProps) => {
  const link = href ?? '';
  const { chatId, actionCardLinks } = useContext(SmoothdownRenderContext);
  const matchRoute = useMatchRoute();
  const { openTab } = useTabs();
  const isSharedChat = !!matchRoute({ to: '/shared-chat/$id' });
  const processedUrl = preprocessUrl(link);

  if (processedUrl === 'littlebird://permissions/screen-recording') {
    if (isSharedChat || !isMac()) return <span>{children}</span>;
    return (
      <a
        href={processedUrl}
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await Screenshot.requestPermission();
        }}
      >
        {children}
      </a>
    );
  }

  const sendDraftInfo = parseSendDraftUrl(processedUrl);
  if (sendDraftInfo.kind === 'sendDraft') {
    if (isSharedChat) return <span>{children}</span>;
    return (
      <SendDraftCard
        draftId={sendDraftInfo.draftId}
        toolkitName={sendDraftInfo.toolkitName}
        accountLabel={sendDraftInfo.accountLabel}
      />
    );
  }

  // Read the href as written: card links are relative, and preprocessing mangles those.
  const actionCardInfo = parseActionCardUrl(link);
  if (actionCardInfo.kind === 'actionCard') {
    // A card belongs to one chat, so content rendered outside of one names nothing.
    if (isSharedChat || !chatId) return <span>{children}</span>;
    if (actionCardLinks === 'chip')
      return (
        <ActionCardMentionChip localId={actionCardInfo.localId}>
          {children}
        </ActionCardMentionChip>
      );
    return (
      <InlineActionCard chatId={chatId} localId={actionCardInfo.localId} />
    );
  }

  const personMentionInfo = parsePersonMentionUrl(link);
  if (personMentionInfo.kind === 'person') {
    if (isSharedChat) return <span>{children}</span>;
    return (
      <PersonMentionChip personId={personMentionInfo.personId}>
        {children}
      </PersonMentionChip>
    );
  }
  if (personMentionInfo.kind === 'group') {
    if (isSharedChat) return <span>{children}</span>;
    return (
      <GroupMentionChip groupName={personMentionInfo.groupName}>
        {children}
      </GroupMentionChip>
    );
  }

  const appMentionInfo = parseAppMentionUrl(link);
  if (appMentionInfo.kind === 'app') {
    if (isSharedChat) return <span>{children}</span>;
    return (
      <AppMentionChip name={appMentionInfo.name}>{children}</AppMentionChip>
    );
  }

  const feedbackDraftInfo = parseFeedbackDraftUrl(processedUrl);
  if (feedbackDraftInfo.kind === 'feedbackDraft') {
    if (isSharedChat) return <span>{children}</span>;
    return <SendFeedbackReportCard draftId={feedbackDraftInfo.draftId} />;
  }

  const webhookSetupInfo = parseWebhookEndpointSetupUrl(processedUrl);
  if (webhookSetupInfo.kind === 'webhookEndpointSetup') {
    // The endpoint is the chat owner's, so a shared chat shows only the text
    if (isSharedChat) return <span>{children}</span>;
    return (
      <WebhookEndpointSetupCard endpointId={webhookSetupInfo.endpointId} />
    );
  }

  const fileLinkInfo = parseFileLinkUrl(link);
  if (fileLinkInfo.kind === 'fileLink') {
    if (isSharedChat || !chatId) return <a href={link}>{children}</a>;
    return (
      <InlineFileLink token={fileLinkInfo.token} href={link}>
        {children}
      </InlineFileLink>
    );
  }

  // Check if this is an OAuth URL
  const oauthInfo = parseOAuthUrl(processedUrl);
  if (oauthInfo.isOAuthUrl && (oauthInfo.flowRef || oauthInfo.redirectUrl)) {
    if (isSharedChat || !chatId) return <span>{children}</span>;
    return (
      <OAuthConnectionCard
        threadId={chatId}
        flowRef={oauthInfo.flowRef}
        redirectUrl={oauthInfo.redirectUrl}
        toolkitName={oauthInfo.toolkitName}
        provider={oauthInfo.provider}
        displayName={oauthInfo.displayName}
        accountLabel={oauthInfo.accountLabel}
        accountId={oauthInfo.accountId}
        serverId={oauthInfo.serverId}
        flowId={oauthInfo.flowId}
      />
    );
  }

  const internalChatMatch = processedUrl.match(
    /^https?:\/\/app\.littlebird\.ai\/chats\/([^/?#]+)/
  );
  if (internalChatMatch) {
    const chatId = internalChatMatch[1];
    const chatPath = `/chats/${chatId}`;
    return (
      <a
        href={processedUrl}
        onClick={(e) => {
          e.preventDefault();
          // Stop the document-level ExternalLinkHandler from also handling this
          // click, which would otherwise navigate again or open a duplicate tab.
          e.stopPropagation();
          if (isNewTabClick(e)) {
            openTab({ path: chatPath });
          } else {
            navigate({ to: chatPath });
          }
        }}
      >
        {children}
      </a>
    );
  }

  const internalRoutinesMatch = processedUrl.match(
    /^https?:\/\/app\.littlebird\.ai\/reports(?:[/?#]|$)/
  );
  if (internalRoutinesMatch) {
    if (isSharedChat) return <span>{children}</span>;
    return (
      <a
        href={processedUrl}
        onClick={(e) => {
          e.preventDefault();
          navigate({ to: '/routines' });
        }}
      >
        {children}
      </a>
    );
  }

  // Native app schemes bypass modal
  if (isNativeSchemeUrl(link)) return <a href={link}>{children}</a>;

  // Internal anchors (#) navigate directly - check before URL parsing
  if (processedUrl.startsWith('#'))
    return <a href={processedUrl}>{children}</a>;

  // Only allow safe http/https URLs
  if (!isOpenableUrl(processedUrl)) return <span>{children}</span>;

  // Relative URLs are never trusted - check original link before preprocessing
  // Prevents bypass via /github.com being normalized to http:///github.com
  const isRelativeUrl = link.startsWith('/');

  // Trusted domains navigate directly (but not relative URLs)
  if (!isRelativeUrl && isTrustedUrl(processedUrl))
    return (
      <a href={processedUrl} rel="noopener noreferrer">
        {children}
      </a>
    );

  if (!config.chat.confirm_external_links)
    return (
      <a href={processedUrl} rel="noopener noreferrer">
        {children}
      </a>
    );

  // Untrusted external links show confirmation modal
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    ModalsManager.show(ExternalLinkModal, { url: processedUrl });
  };

  // Intercept middle-click (auxclick) to prevent bypassing modal
  const handleAuxClick = (e: React.MouseEvent) => {
    if (e.button === 1) {
      e.preventDefault();
      e.stopPropagation();
      ModalsManager.show(ExternalLinkModal, {
        url: processedUrl,
      });
    }
  };

  // Intercept right-click to prevent native context menu bypass
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    ModalsManager.show(ExternalLinkModal, { url: processedUrl });
  };

  return (
    <a
      href={processedUrl}
      onClick={handleClick}
      onAuxClick={handleAuxClick}
      onContextMenu={handleContextMenu}
      rel="noopener noreferrer"
      className="cursor-pointer"
    >
      {children}
    </a>
  );
});

function preprocessUrl(url: string) {
  if (url.startsWith('http.')) {
    url = url.replace('http.', 'http://');
  }

  if (url.startsWith('https.')) {
    url = url.replace('https.', 'https://');
  }

  try {
    new URL(url);
    return url;
  } catch {
    // No valid protocol, check if it looks like a URL that needs http://
    if (!url.startsWith('#')) {
      return 'http://' + url;
    }
    // Return as-is for anchors (#)
    return url;
  }
}
